import React, { useEffect } from "react";
import { AxiosError } from "axios";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { compose } from "redux";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  authAxios as axios,
  SL_MEMBER_GROUP,
  useAuth,
  UserGroupAccountTypeMap,
} from "shared-auth";
import { LaunchDarklyProvider } from "shared-services";
import { getAlertMessageTexts } from "component-library";
import { useTranslation } from "i18n";
import { ErrorCode, MemberGroups } from "shared-types";
import { IApplicationState } from "store/index";
import { GetCommonTranslation } from "store/translation/translation.types";
import {
  hidePopup,
  setLoading,
  setLocale,
  showPopup,
} from "store/ui/ui.actions";
import {
  HidePopup,
  IUIState,
  PopupType,
  SetLoading,
  SetLocale,
  ShowPopup,
} from "store/ui/ui.types";
import applicationMap from "services/applicationMap";
import changeTrackingRegistryService from "services/changeTrackingRegistryService";
import Content from "app-shell/Content/Content";
import withGlobalSpinner from "hoc/withGlobalSpinner";
import withZendesk from "hoc/withZendesk";
import withNavigator from "hoc/withNavigator";
import withUnsavedChangesNavigationPrevention from "hoc/withUnsavedChangesNavigationPrevention";
import Popups from "components/Popups/Popups";
import { getCommonTranslation } from "store/translation/translation.actions";
import { setUserData } from "store/user/user.actions";
import { SetUserData } from "store/user/user.types";

export interface IAppActions {
  setLoading: SetLoading;
  setLocale: SetLocale;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  getCommonTranslation: GetCommonTranslation;
  setUserData: SetUserData;
}

export interface IAppStoreProps {
  ui: IUIState;
  locale: string;
  isLoading: boolean;
  isCommonTranslationLoaded: boolean;
  isUserLoaded: boolean;
}

export interface IAppProps
  extends IAppStoreProps,
    IAppActions,
    RouteComponentProps {}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC<IAppProps> = ({
  location,
  history,
  hidePopup,
  showPopup,
  setUserData,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();

  useEffect(() => {
    if (auth?.user?.profile?.sid) {
      setUserData({
        id: auth.user.profile.sid,
        username: auth.user.profile.name ?? "UnknownName",
        type: UserGroupAccountTypeMap[
          auth.user.profile[SL_MEMBER_GROUP] as MemberGroups
        ],
        email: auth.user.profile.email ?? "UnknownEmail@spotlight.com",
        forenames: auth.user.profile.given_name ?? "UnknownForename",
        surname: auth.user.profile.family_name ?? "UnknownSurname",
      });
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const errorStatusCode = error.response?.status;

        if (errorStatusCode === ErrorCode.MissingCountryCode) {
          showPopup({
            type: PopupType.ErrorPopup,
            data: {
              title: t("performer:home.updateDetails.popup.title"),
              description: t("performer:home.updateDetails.popup.description"),
              closeButtonText: t("common:button.label.continue"),
              hidePopup: () => {
                history.push({
                  pathname: applicationMap.routes.settingsMyDetails(),
                  search: `?returnUrl=${encodeURIComponent(location.pathname)}`,
                });
                hidePopup();
              },
            },
          });

          return;
        }

        if (
          error.response?.config?.params?.skipGenericErrorHandling ||
          error.config?.params?.skipGenericErrorHandling
        ) {
          return Promise.reject(error as AxiosError);
        }

        const isAuthError = errorStatusCode === 401;

        const hidePopUpClick = isAuthError
          ? () => {
              auth.signinRedirect();
            }
          : () => window.location.reload();
        const i18nErrorStatusCode = getAlertMessageTexts(t, errorStatusCode);

        showPopup({
          type: PopupType.ErrorPopup,
          data: {
            closeButtonText: t("common:button.label.close"),
            title: i18nErrorStatusCode.title,
            description: i18nErrorStatusCode.description,
            hidePopup: hidePopUpClick,
          },
        });

        return Promise.reject(error as AxiosError);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [history, location.pathname, hidePopup, showPopup, auth, setUserData, t]);

  return (
    <QueryClientProvider client={queryClient}>
      <LaunchDarklyProvider>
        <Content />
        <Popups />
      </LaunchDarklyProvider>
    </QueryClientProvider>
  );
};

const mapDispatchToProps = {
  setLoading,
  setLocale,
  showPopup,
  hidePopup,
  getCommonTranslation,
  setUserData,
};

const mapStateToProps = (state: IApplicationState): IAppStoreProps => ({
  ui: state.ui,
  locale: state.ui.locale,
  isLoading: state.ui.isLoading && !state.user.isLoaded,
  isCommonTranslationLoaded: state.translation.isCommonLoaded,
  isUserLoaded: state.user.isLoaded,
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withGlobalSpinner,
  withUnsavedChangesNavigationPrevention(
    window,
    changeTrackingRegistryService.getUnsavedChange
  ),
  withNavigator,
  withZendesk
)(App) as React.ComponentType;
